
import Vue, { PropType } from 'vue';
import { LogoUploadHeader } from './Header';
import { ErrorNotification } from '../../libs/proof-approval/ErrorNotification';
import {
  CvGrid,
  CvColumn,
  CvRow,
  CvInlineLoading,
  CvLoading,
} from '@carbon/vue';
import QuestionAboutWhite from '@/components/QuestionAboutWhiteColor/QuestionAboutWhite.vue';
import QuestionAboutWhiteReplace from '@/components/QuestionAboutWhiteColor/QuestionAboutWhiteReplace.vue';
import { WizardPayload, PaggedCollection } from '../logo-library/typings';
import customersAPI from '@/services/CustomersService';
import customerDataAPI from '@/services/CustomerDataService';
import imageProcessingAPI from '@/services/FileService';
import { UploadArtworkDialog } from './UploadArtworkDialog';
import { throttle } from 'throttle-debounce';
import axios, { AxiosError } from 'axios';
import { base64StringToBlob } from 'blob-util';
import { SearchByCriteria } from '../logo-library/typings';
import { v4 as uuidv4 } from 'uuid';
import { SupportedLangCodes } from '../../constants/languageCodes';
import {
  ImageProcessingEngineVersion,
  DoSmallDetailsPart,
} from '../../constants/imageProcessingEngine';
import {
  Artwork,
  ArtworkResult,
  VFileUploaderFile,
} from '@/components/w2pFileUploader';
import { mapState } from 'pinia';
import { SetArtworkBackgroundCommand } from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import {
  SmallDetailsLoader,
  SmallDetailsOneLogo,
} from '@/components/SmallDetails';
import { lineWidth } from '@/constants/areaLineWidth';
import { px2mm } from '@/utils';
import { SmallDetailsResult } from '@/typings';
import {
  CheckImageSmallDetailsCommand,
  NewLogoImprovementRequestForm,
  SmallDetailsPayload,
} from '@/typings/SmallDetails';
import {
  mm2px,
  ProductPrintPosition,
  SmallDetailsLimitations,
} from '@midocean/webapps-shared-domain';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { useStore as useLangStore } from '@/stores/lang';
import { useStore as useLangStoreV2 } from '@/stores/v2/lang';
import { useStore as useShapeStoreV2 } from '@/stores/v2/shape';
import SmallDetailsSingleLogoModal from '../import-wizard-v2/components/SmallDetails/SmallDetailsSingleLogo/SmallDetailsSingleLogoModal.vue';
import { LOGO_IMPROVEMENT_FLOW_ACTIVE } from '@/constants/smallDetails';
import LogoImprovementRequestSuccessModal from '../import-wizard-v2/components/SmallDetails/LogoImprovementRequestSuccessModal/LogoImprovementRequestSuccessModal.vue';
import {
  BACKGROUND_IMAGE_DPI,
  BACKGROUND_IMAGE_W,
} from '../import-wizard-v2/constants';
import { fileTypeValidatorSmallDetails } from '../import-wizard-v2/validators';
import { ArtworkPayload } from '../import-wizard/typings/pintura';

export default Vue.extend({
  components: {
    CvGrid,
    CvRow,
    CvColumn,
    CvInlineLoading,
    CvLoading,
    LogoUploadHeader,
    UploadArtworkDialog,
    SmallDetailsLoader,
    SmallDetailsOneLogo,
    ErrorNotification,
    QuestionAboutWhite,
    QuestionAboutWhiteReplace,
    SmallDetailsSingleLogoModal,
    LogoImprovementRequestSuccessModal,
  },
  props: {
    sapCustomerId: {
      type: String,
      required: true,
    },
    env: {
      type: Object,
      default: () => ({ context: 'myAccount' }),
    },
    lang: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
    },
    useVersion2: {
      type: Boolean,
      required: false,
      default: false,
    },
    sapCustomerEmail: {
      type: String,
      required: false,
      default: '',
    },
    region: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      smallDetailsResult: null as SmallDetailsResult | null,
      wizardPayload: null as WizardPayload | null,
      limitation: {} as SmallDetailsLimitations | undefined,
      thumbEl: null as unknown as any,
      customerId: '',
      isLoading: false,
      showSmallDetailsLoader: false,
      isSmallDetailsOpen: false,
      isDeleting: false,
      activeDialog: 'UploadArtworkDialog',
      selectedArtworkId: '',
      uuidv4,
      isNotificationVisible: false,
      errorMessage: '',
      LOGO_IMPROVEMENT_FLOW_ACTIVE,
      isCreatingLogoImprovementRequest: false,
      fileTypeValid: true,
    };
  },

  computed: {
    ...mapState(useProductStore, ['isSublimationTechnique']),
    ...mapState(useProductStoreV2, [
      'isSublimationTechnique',
      'data',
      'activePosition',
      'unavailablePrintPositions',
    ]),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useLangStoreV2, ['languageInUse']),
  },

  watch: {
    async languageInUse() {
      if (process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false') {
        var translationsJson = await customersAPI.getTranslations(
          this.languageInUse
        );
        this.$i18n.setLocaleMessage(this.languageInUse, translationsJson.data);
      }
    },
  },

  async mounted() {
    await this.populateCustomerId();
  },

  methods: {
    async keepWhite() {
      if (this.isSublimationTechnique) {
        this.$modal.hide('question-about-white-replace');
      } else {
        this.$modal.hide('question-about-white');
      }

      await this.finalizePayloadProcessing();
    },
    async removeWhite() {
      if (this.isSublimationTechnique) {
        this.$modal.hide('question-about-white-replace');
      } else {
        this.$modal.hide('question-about-white');
      }

      const command: SetArtworkBackgroundCommand = {
        isFullBackground: true,
        color: 2,
      };
      const { data } = await imageProcessingAPI.changeLogoBackground(
        this.wizardPayload?.pngFile || '',
        command
      );

      if (this.wizardPayload) {
        this.wizardPayload.pngFile = base64StringToBlob(
          data.pngFile,
          'image/png'
        );
        this.wizardPayload.traditionalTechniquePngFile = base64StringToBlob(
          data.pngFile,
          'image/png'
        );
        this.wizardPayload.pngFileWithoutConverters = base64StringToBlob(
          data.pngFile,
          'image/png'
        );
        this.wizardPayload.pngHeightPixels = data.heightInPx;
        this.wizardPayload.pngWidthPixels = data.widthInPx;

        if (data.isAllWhiteRemoved) {
          const whiteColorIndex =
            this.wizardPayload.pmsColorsInPngFile.findIndex(
              (c) => c.name === 'Pure White'
            );
          if (whiteColorIndex !== -1) {
            this.wizardPayload.pmsColorsInPngFile.splice(whiteColorIndex, 1);
          }
        }
      }

      await this.finalizePayloadProcessing();
    },
    async finalizePayloadProcessing() {
      if (DoSmallDetailsPart && this.limitation) {
        this.isSmallDetailsOpen = false;
        this.smallDetailsResult = null;

        this.showSmallDetailsLoader = true;
        const productPrintPosition = this.data.productPrintPositions.find(
          ({ name }) => name === this.activePosition.name
        );

        if (!productPrintPosition) return false;

        const selectedTechniqueId = this.unavailablePrintPositions.find(
          ({ positionId }) => positionId === productPrintPosition.id
        )?.technique.id;

        const printTechnique = productPrintPosition.printTechniques.find(
          ({ id }) => id === selectedTechniqueId
        );

        if (!printTechnique) return false;

        for (const logoValidator of printTechnique.logoValidators) {
          if (logoValidator.type.trim() === 'FileType' && this.wizardPayload) {
            const results = fileTypeValidatorSmallDetails(
              logoValidator.configuration,
              this.wizardPayload,
              this.activePosition.name || ''
            );
            this.fileTypeValid = results?.validatorColor === 'Green';
          }
        }
        if (!this.fileTypeValid) {
          this.showSmallDetailsLoader = false;
          this.onProceedWithLogo(undefined);
        } else {
          await this.prepareSmallDetailsResults(
            this.wizardPayload as WizardPayload,
            this.limitation as SmallDetailsLimitations
          );
          this.showSmallDetailsLoader = false;
        }
      } else {
        // TO DO: Remove this flow when small details is finised
        this.wizardPayloadReady(this.wizardPayload as unknown as WizardPayload);
      }
    },

    async populateCustomerId() {
      this.isLoading = true;
      try {
        const { data } = await customerDataAPI.getCustomerId({
          externalCustomerId: this.sapCustomerId,
        });

        this.customerId = data.customerId;
      } catch (error) {
        this.handleError(error);

        return Promise.resolve(uuidv4());
      } finally {
        this.isLoading = false;
      }
    },
    onBackToLibrary() {
      this.isSmallDetailsOpen = false;
      this.selectedArtworkId = '';
      if (this.useVersion2) {
        this.$modal.hide('small-details-single-logo-modal');
      } else {
        this.$modal.hide('small-details-one-logo-modal');
      }
    },
    async onUploadReady(payload: ArtworkResult) {
      this.wizardPayload = {
        ...payload,
        pngFileWithoutConverters: payload.pngFile,
        cmykPdfFileUrl: payload.cmykPdfFileUrl ? payload.cmykPdfFileUrl : '',
      } as unknown as WizardPayload;
      await this.processWizardPayload();
    },
    onUploadNewLogo() {
      this.isSmallDetailsOpen = false;
      this.selectedArtworkId = '';
      this.activeDialog = 'UploadArtworkDialog';
    },
    onProceedWithLogo(smallDetailsPayload: SmallDetailsPayload | undefined) {
      if (this.wizardPayload) {
        this.wizardPayload.smallDetailsResult = smallDetailsPayload;
        this.wizardPayloadReady(this.wizardPayload);
      }
    },
    async onRequestLogoImprovement(formData: {
      comment: string;
      improvementOption: string;
      improvementOptionKey: string;
    }) {
      this.isCreatingLogoImprovementRequest = true;

      const productStoreV2 = useProductStoreV2();

      const position = productStoreV2.activePosition;
      const { widthInMM, heightInMM } = await this.calculateArtworkSize(
        this.wizardPayload as WizardPayload
      );

      const currentPosition = productStoreV2.printPositions.find(
        (pos) => pos.name === position.name
      );

      const newLogoImprovementForm = {
        customerNumber: this.sapCustomerId,
        productSapCode: productStoreV2.data.name,
        email: this.sapCustomerEmail,
        region: this.region,
        language: this.languageInUse,
        logos: [
          {
            originalFileName: this.wizardPayload?.fileName,
            title: this.wizardPayload?.title,
            printTechniqueSapCode:
              currentPosition?.printTechniques[0].printTechnique.code,
            printPositionSapCode: position.name,
            printSizeWidthMm: widthInMM,
            printSizeHeightMm: heightInMM,
            improvementOption: formData.improvementOption,
            comment: formData.comment,
            improvementOptionKey: formData.improvementOptionKey,
          },
        ],
      } as NewLogoImprovementRequestForm;
      if (this.wizardPayload?.pngFile) {
        const response = await fetch(this.wizardPayload.originalFileUrl);
        const blob = await response.blob();
        let file = new File([blob], this.wizardPayload?.fileName, {
          type: blob.type,
          lastModified: Date.now(),
        });

        await customersAPI
          .submitLogoImprovementRequest([file], newLogoImprovementForm)
          .then(() => {
            this.isCreatingLogoImprovementRequest = false;
            this.$modal.hide('small-details-single-logo-modal');
            this.$modal.show('logo-improvement-request-success-modal');
          });
      } else {
        this.isCreatingLogoImprovementRequest = false;
      }
    },

    async dispatchAction(action: string) {
      if (this.env.context === 'wizard' && action === 'import') {
        this.wizardPayload = await this.createPersonalizationArtwork();
        await this.processWizardPayload();
        return;
      }
      if (action === 'cancel') {
        this.selectedArtworkId = '';
        return;
      }

      if (['upload-success', 'edit-success'].includes(action)) {
        this.selectedArtworkId = '';
        this.activeDialog = '';
      }
    },

    async processWizardPayload() {
      const productStore = this.useVersion2
        ? useProductStoreV2()
        : useProductStore();
      this.limitation = productStore.activePosition.limitation;

      if (
        productStore.isLaserConverter() &&
        this.wizardPayload?.pmsColorsInPngFile.findIndex((c) =>
          c.name.includes('Pure White')
        ) !== -1
      ) {
        this.$modal.show('question-about-white');
      } else if (
        this.isSublimationTechnique &&
        this.wizardPayload?.pmsColorsInPngFile.findIndex((c) =>
          c.name.includes('Pure White')
        ) !== -1
      ) {
        this.$modal.show('question-about-white-replace');
      } else {
        await this.finalizePayloadProcessing();
      }
    },

    async prepareSmallDetailsResults(
      payload: WizardPayload,
      limitation: SmallDetailsLimitations
    ) {
      const { widthInMM } = await this.calculateArtworkSize(payload);

      this.smallDetailsResult = await this.smallDetailsCheck(
        payload.id,
        widthInMM,
        limitation.positiveLimitMM,
        limitation.negativeLimitMM,
        payload.pngFile,
        limitation.ignoreColors ||
          Array.from(new Set(payload.pmsColorsInPngFile.map((obj) => obj.name)))
            .length === 1
      );

      this.isSmallDetailsOpen = true;
    },

    async smallDetailsCheck(
      id: string,
      logoWidthInMm: number,
      positiveLimitInMm: number,
      negativeLimitInMm: number,
      file: Blob,
      ignoreColors: boolean
    ): Promise<SmallDetailsResult | null> {
      const command: CheckImageSmallDetailsCommand = {
        id,
        logoWidthInMm,
        positiveLimitInMm,
        negativeLimitInMm,
        ignoreColors,
      };
      return imageProcessingAPI
        .checkImageSmallDetails(command, file)
        .catch((e) => {
          this.handleError(e);
          console.log(e);
          return null;
        })
        .then((resp) => {
          return resp && resp.data;
        })
        .finally(() => (this.showSmallDetailsLoader = false));
    },

    async convertToBlob(fileUrl: string) {
      return axios({
        url: fileUrl,
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        return response.data;
      });
    },

    async createPersonalizationArtwork(): Promise<WizardPayload> {
      let payload: WizardPayload = {} as WizardPayload;
      this.isLoading = true;
      try {
        const { data } = await customersAPI.getById(this.selectedArtworkId);

        if (data.engineVersion === ImageProcessingEngineVersion) {
          const pngFileBlob = await this.convertToBlob(data.pngFileUrl);
          const traditionalTechPngFileBlob = data.traditionalTechniquePngFileUrl
            ? await this.convertToBlob(data.traditionalTechniquePngFileUrl)
            : '';

          payload = {
            ...data,
            customerArtworkId: data.id,
            pngFile: pngFileBlob,
            pngFileWithoutConverters: pngFileBlob,
            pmsColorsInPngFile: data.pmsColorsInPngFile,
            personalizationArtworkId: '',
            traditionalTechniquePngFile: traditionalTechPngFileBlob,
          };
          const resp = await customersAPI.createDraftPersonalizationArtwork(
            payload.customerArtworkId
          );
          payload.personalizationArtworkId = resp.data.id;
          payload.id = resp.data.id;
          payload.originalWidthMM = resp.data.originalWidthMM;
          payload.originalHeightMM = resp.data.originalHeightMM;
          payload.originalHorizontalResolutionDPIs =
            resp.data.originalHorizontalResolutionDPIs;
          payload.originalVerticalResolutionDPIs =
            resp.data.originalVerticalResolutionDPIs;
        } else {
          const artwork: Artwork = {
            shouldSaveInLogoLibrary: true,
            shouldSaveDraftForWizard: true,
            title: data.title,
            fileName: data.fileName,
            externalCustomerId: this.sapCustomerId,
            customerId: this.customerId,
            customerArtworkId: data.id,
            currentEngineVersion: ImageProcessingEngineVersion,
          };
          const originalFileBlob = await this.convertToBlob(
            data.originalFileUrl
          );

          const fileForProcessing: VFileUploaderFile = {
            file: new File([originalFileBlob], data.fileName),
            invalidMessage: '',
            invalidMessageTitle: '',
            state: '',
          };

          const response = await imageProcessingAPI.upload(
            artwork,
            fileForProcessing
          );

          payload = {
            ...response.data,
            id: response.data.personalizationArtworkId,
            title: data.title,
            fileName: data.fileName,
            thumbnailUrl: data.thumbnailUrl,
            tags: data.tags,
            pngFileUrl: data.pngFileUrl,
            pngFile: base64StringToBlob(response.data.pngFile, 'image/png'),
            pngFileWithoutConverters: base64StringToBlob(
              response.data.pngFile,
              'image/png'
            ),
            traditionalTechniquePngFileUrl: data.traditionalTechniquePngFileUrl,
            traditionalTechniquePngFile: response.data
              .traditionalTechniquePngFile
              ? base64StringToBlob(
                  response.data.traditionalTechniquePngFile,
                  'image/png'
                )
              : '',
          };
        }
      } catch (e: any) {
        console.log(e);
        this.isLoading = false;
        if (e.response.status === 409) {
          if (
            e.response.data.error.message ===
            'Provided PDF contains more than 10 pages, which is not allowed. Please provide PDF with less than 10 pages.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.mutlipage-pdf-exception'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork. Processing would require too much time. Please provide simplified version.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.timeout-exception'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.image-magick'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.ghost-script'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.resolution-error'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.argument-exception'
            ).toString();
          }
          this.isNotificationVisible = true;
        } else {
          this.handleError(e);
          throw e;
        }
      }

      this.isLoading = false;
      return payload;
    },

    wizardPayloadReady(payload: WizardPayload) {
      this.$emit('logo-library:import', payload);
    },

    async calculateArtworkSize(payload: WizardPayload) {
      if (this.useVersion2) {
        // Version 2 uses the new shape store and the new way of calculating artwork size
        //Avoid usding different logic to calculate artwork size and different small details results
        let widthInMM = 0;
        let heightInMM = 0;

        const currentProductStore = useProductStoreV2();
        const positionId = currentProductStore.activePosition.id;
        const shapeStoreV2 = useShapeStoreV2();
        await shapeStoreV2.addArtworkPayload(
          {
            ...payload,
            isRoundLogo: false,
            customerId: this.customerId,
            cmykPdfFileUrl: '',
            processed: '',
            variantArtworkImages: [],
          },
          positionId as string
        );

        const currentShape = shapeStoreV2.shapeById(payload.id);

        if (currentShape) {
          widthInMM = currentShape.widthOnProductMM;
          heightInMM = currentShape.heightOnProductMM;

          shapeStoreV2.removeShape(payload.id);
        }

        return {
          widthInMM,
          heightInMM,
        };
      } else {
        // TODO - remove old store after version 1 is no longer used
        const artworkSpaceConsumed = 97; // value in percents
        let calcArtworkWidth = 0;
        let calcArtworkHeight = 0;
        const CORRECTION_FACTOR = 0;
        const productStore = useProductStore();

        const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
          productStore.activePosition;

        const webImageWidthPx =
          originalImageWidthPx &&
          originalImageWebDpi &&
          originalImageDpi &&
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
        let rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

        const aspectRatioWH = payload.pngWidthPixels / payload.pngHeightPixels;
        const aspectRatioHW = payload.pngHeightPixels / payload.pngWidthPixels;
        const positionId = productStore.activePosition.id;
        if (positionId) {
          const currentPrintPosition = productStore.printPositions?.find(
            (p) => {
              return p.id === positionId;
            }
          );
          if (currentPrintPosition) {
            var printArea = productStore.drawPoints(positionId);
          }
          if (
            currentPrintPosition &&
            currentPrintPosition.printArea?.shapeType === 'Rectangle'
          ) {
            const areaRect = {
              x: printArea.x + rectLineWidth / 2,
              y: printArea.y + rectLineWidth / 2,
              w:
                (this.useVersion2 ? printArea.width : printArea.w) -
                rectLineWidth,
              h:
                (this.useVersion2 ? printArea.height : printArea.h) -
                rectLineWidth,
            };
            const artworkRatio =
              payload.pngWidthPixels / payload.pngHeightPixels;
            const areaRatio = areaRect.w / areaRect.h;
            // Artwork is more landscape than area - fit to width
            if (artworkRatio > areaRatio) {
              calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
              calcArtworkHeight =
                (calcArtworkWidth / payload.pngWidthPixels) *
                payload.pngHeightPixels;
            }
            // Artwork is more portrait than area - fit to height
            else {
              calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
              calcArtworkWidth =
                (calcArtworkHeight / payload.pngHeightPixels) *
                payload.pngWidthPixels;
            }
          }
          if (
            positionId &&
            currentPrintPosition &&
            currentPrintPosition.printArea?.shapeType === 'Ellipse'
          ) {
            const min =
              printArea.rx < printArea.ry ? printArea.rx : printArea.ry;
            let size = 0;
            if (Math.abs(printArea.ry - printArea.rx) > 630) {
              size = min * 2;
            } else {
              size = min * 1.6;
            }
            if (aspectRatioWH >= aspectRatioHW) {
              calcArtworkWidth = (size * (artworkSpaceConsumed - 15)) / 100;
              calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
            } else {
              calcArtworkHeight = (size * (artworkSpaceConsumed - 15)) / 100;
              calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
            }
          }
          if (
            currentPrintPosition &&
            currentPrintPosition.printArea?.shapeType === 'Polygon'
          ) {
            const size = productStore.polygonDetails(positionId).minDistance;
            if (aspectRatioWH >= aspectRatioHW) {
              calcArtworkWidth = (size * artworkSpaceConsumed) / 100;
              calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
            } else {
              calcArtworkHeight = (size * artworkSpaceConsumed) / 100;
              calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
            }
          }
        }
        const dpi = originalImageWebDpi || originalImageDpi || 300;
        return {
          widthInMM: px2mm(calcArtworkWidth, dpi),
          heightInMM: px2mm(calcArtworkHeight, dpi),
        };
      }
    },

    handleError(error: any) {
      if (error && error.response && error.response.status === 400) {
        this.errorMessage = 'notification-inline.user-input-error';
      } else if (error && error.response && error.response.status === 404) {
        this.errorMessage = 'notification-inline.item-not-found';
      } else {
        this.errorMessage = '';
      }

      this.isNotificationVisible = true;
    },
  },
});
